import { useEffect, useState, type FormEvent } from "react";
import { signup, acceptDataNotice } from "../users/me-api";
import { signInWithEmail, sendVerificationEmail, humanizeAuthError } from "../auth";
import { trackEvent } from "../lib/analytics";

interface Props {
  inviteGateEnabled: boolean;
  /** Pre-fills the invite code from an ?invite=CODE email link. */
  initialInviteCode?: string;
  /**
   * Pre-fill name/email too (arrive slightly after initialInviteCode — a
   * follow-up lookup, not part of the initial URL parse), so an invited user
   * mostly just needs to add a password. Full name stays editable (nothing
   * depends on it matching); email is locked — editing it would just fail
   * server-side, since it must match the invite.
   */
  initialFullName?: string;
  initialEmail?: string;
  onSignedUp: () => void;
  onBackToLogin: () => void;
}

export function SignupScreen({
  inviteGateEnabled,
  initialInviteCode,
  initialFullName,
  initialEmail,
  onSignedUp,
  onBackToLogin,
}: Props) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(initialInviteCode?.toUpperCase() ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // initialFullName/initialEmail resolve after a network round-trip, later than
  // the initial render — sync them in once they arrive. Guarded so a fast typist
  // filling the field before the lookup resolves doesn't get overwritten.
  useEffect(() => {
    if (initialFullName) setFullName((cur) => (cur === "" ? initialFullName : cur));
  }, [initialFullName]);
  useEffect(() => {
    if (initialEmail) setEmail((cur) => (cur === "" ? initialEmail : cur));
  }, [initialEmail]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const trimmedCode = inviteCode.trim();
    const invited = trimmedCode.length > 0;
    try {
      await signup({
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        password,
        ...(invited ? { inviteCode: trimmedCode } : {}),
      });
      await signInWithEmail(email.trim().toLowerCase(), password);
      // Invited users are pre-verified server-side and skip both guardrails, so
      // no verification email is sent. Everyone else gets the verify-email gate.
      if (!invited) await sendVerificationEmail().catch(() => undefined);
      await acceptDataNotice();
      trackEvent("sign_up", { method: "password", invited });
      onSignedUp();
    } catch (err) {
      const code = (err as { code?: string }).code;
      const body = (err as { body?: { error?: string } }).body;
      setError(body?.error ?? humanizeAuthError(code) ?? (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1>Create your account</h1>
        <p className="subtitle">Tell us who you are to start your free canvas.</p>
        <form onSubmit={handleSubmit}>
          <label htmlFor="signup-name">Full name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            minLength={2}
            maxLength={100}
            required
          />
          <label htmlFor="signup-email">Email</label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={!!initialEmail}
          />
          {initialEmail && (
            <p style={{ fontSize: 13, color: "var(--ink-4)", margin: "-6px 0 0" }}>From your invite link.</p>
          )}
          <label htmlFor="signup-password">Password (8+ characters)</label>
          <input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            placeholder="Password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <label htmlFor="signup-invite">
            {inviteGateEnabled ? "Invite code" : "Invite code (optional)"}
          </label>
          <input
            id="signup-invite"
            type="text"
            placeholder="Invite code"
            autoCapitalize="characters"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            required={inviteGateEnabled}
            disabled={!!initialInviteCode}
          />
          {initialInviteCode && (
            <p style={{ fontSize: 13, color: "var(--ink-4)", margin: "-6px 0 0" }}>From your invite link.</p>
          )}
          <button type="submit" disabled={submitting}>
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>
        {error && <div className="banner error">{error}</div>}
        <p className="footnote">
          Already have an account?{" "}
          <button className="btn btn--text" onClick={onBackToLogin}>
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
